import React, { useEffect, useRef, useState } from "react";
import { Body } from "@sprout/typography";
import ArrowLeftIcon from "@sprout/icons/arrow_left";
import "./login_security_code.scss";
import { CODE_LENGTH, CodeInput } from "@components/code_input";
import useTranslation from "@hooks/use_translation";
import { PrimaryButton, TertiaryButton } from "@sprout/buttons";
import { useMediaQuery } from "usehooks-ts";
import classNames from "classnames";
import { router } from "@inertiajs/react";
import useDevice from "@hooks/use_device";

type CodeFormProps = {
  submitCodePath: string;
  email: string;
  setShowCode: (showCode: boolean) => void;
};

const LoginSecurityCode = ({ email, setShowCode, submitCodePath }: CodeFormProps) => {
  const { t } = useTranslation("misc");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [securityCode, setSecurityCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { deviceType } = useDevice();

  const isSmallScreen = useMediaQuery("(max-width: 480px");
  const buttonContainerClassNames = classNames({
    "flex flex-col": isSmallScreen,
  });

  const canSubmit = securityCode.length === CODE_LENGTH;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitting || !canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    router.post(
      submitCodePath,
      {
        code: securityCode,
        email: email,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        device_type: deviceType,
      },
      {
        onError: (errors) => {
          setError(errors["code"] || errors["_server"] || t("apologeticDefaultError"));
        },
        onFinish: () => setIsSubmitting(false),
        onSuccess: () => router.clearHistory(),
      },
    );
  };

  // auto-submit when the code is filled
  useEffect(() => {
    if (canSubmit) {
      buttonRef.current?.click();
    }
  }, [securityCode, canSubmit]);

  return (
    <form className="login__form" onSubmit={submit}>
      <div className="login__code-header">
        <TertiaryButton onClick={() => setShowCode(false)}>
          <ArrowLeftIcon />
          {t("back")}
        </TertiaryButton>
        <div>
          <Body
            dangerouslySetInnerHTML={{
              __html: t("login.codeSent", { email }),
            }}
          />
        </div>
      </div>
      <div className={`login-security-code ${error ? "login-security-code--error" : ""}`}>
        <CodeInput
          hasError={!!error}
          errorId="login-security-code-error"
          onChange={setSecurityCode}
        />
        {error && (
          <p
            id="login-security-code-error"
            className="helper-text helper-text--error"
            aria-live="polite"
          >
            {error}
          </p>
        )}
      </div>
      <Body>{t("login.stillWaiting")}</Body>
      <div className={buttonContainerClassNames}>
        <PrimaryButton loading={isSubmitting} type="submit" disabled={!canSubmit} ref={buttonRef}>
          {t("submit")}
        </PrimaryButton>
      </div>
    </form>
  );
};

export default LoginSecurityCode;
